import { Button, ButtonProps } from '@components/button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconDuplicate } from '@components/icons/duplicate';
import { showSuccess } from '@components/toaster';
import { useClipboard } from '@hooks/use-clipboard';
import { useTimeoutToggleState } from '@hooks/use-timeout-state';
import { useScopedTranslate } from '@locale/translate';

export type CopyButtonProps = ButtonProps & {
  copyLabel?: string;
  hasToast?: boolean;
  getText: () => string;
  iconOnly?: boolean;
};

export const CopyButton = ({
  copyLabel,
  hasToast,
  getText,
  iconOnly = false,
  onClick,
  ...rest
}: CopyButtonProps) => {
  const { t } = useScopedTranslate(locale);
  const [isCopied, setIsCopied] = useTimeoutToggleState({ timeout: 2600 });
  const { onCopy: copyToClipboard } = useClipboard();
  const copyText = copyLabel ?? t('copy');
  const statusText = isCopied ? t('copied') : copyText;
  const handleCopy = (event: React.MouseEvent<HTMLButtonElement>) => {
    const text = getText();

    copyToClipboard(text);
    setIsCopied();
    onClick?.(event);
    if (hasToast) {
      showSuccess(t('contentCopied'));
    }
  };

  return (
    <Button
      iconLeft={isCopied ? <IconCheckmark /> : <IconDuplicate />}
      variant="secondary"
      onClick={handleCopy}
      {...(!iconOnly ? { text: statusText } : {})}
      {...rest}
    />
  );
};

const locale = {
  en: {
    copy: 'Copy',
    copied: 'Copied',
    contentCopied: 'Content copied to clipboard',
  },
};
